import { AccountApi, ApiClient, IAccount, IAccountUserLevel, ISessionResponse, IShip, ShipApi } from "../../Types"
import type { TranslateFn } from "../ShipData/Context"

// The flag a companion reconnect adds to its redirect when it cannot reach its primary
// (`companionUnreachable` in `ShipApi.swift`). Mirrors `companion_disconnected`, which
// `AppView` reads the same way.
const COMPANION_ERROR_PARAM = "companion_error";

// Whether this page was reached because a stored companion pairing could not be
// reached, clearing the flag as it reads it.
//
// #1388: that redirect used to be indistinguishable from opening the account page
// normally, and it happens on EVERY launch while the pairing is stored -- so a
// companion whose primary had moved was returned here again and again with nothing
// said. Consumed rather than merely read so a reload doesn't keep re-asserting a
// failure the user has already seen, exactly as `companion_disconnected` does.
export function takeCompanionReconnectNotice(): boolean {
  if (typeof window == "undefined") {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  if (!params.get(COMPANION_ERROR_PARAM)) {
    return false;
  }
  params.delete(COMPANION_ERROR_PARAM);
  const query = params.toString();
  window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  return true;
}

// Which account-edit form the subscription tab is currently showing.
export enum AccountSettingsEditAccount {
  none = 0,
  name = 1,
  password = 2,
}

// Shared data layer for AccountSettingsView and its child views: the network
// requests plus the ship-ownership partitioning. Extracted so the wire contract
// and the filtering branches can be unit-tested without rendering the view.
export const AccountSettingsViewData = {
  loadLocalShips(api: ApiClient) {
    return new ShipApi(api).ships.local({ params: {} }).call();
  },
  loadAccountUsers(api: ApiClient) {
    return new AccountApi(api).account.users({ params: {} }).call();
  },
  loadAccountPlan(api: ApiClient) {
    return new AccountApi(api).account.plan({ params: {} }).call();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  updateAccountUser(api: ApiClient, values: any): Promise<ISessionResponse> {
    return new AccountApi(api).account.updateUser({ params: {}, body: values }).call();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  updatePassword(api: ApiClient, values: any): Promise<ISessionResponse> {
    return new AccountApi(api).account.updatePassword({ params: {}, body: values }).call();
  },
  accountShips(ships: IShip[] | undefined, account: IAccount | undefined): IShip[] | undefined {
    return ships?.filter((s) => !account || s.accountId == account.id);
  },
  sharedShips(ships: IShip[] | undefined, account: IAccount | undefined): IShip[] | undefined {
    return ships?.filter((s) => account && s.accountId != account.id);
  },

  accountUserLevelText(tt: TranslateFn, level: IAccountUserLevel): string {
    switch (level) {
      case IAccountUserLevel.disabled: return tt("account_user_level_disabled");
      case IAccountUserLevel.user: return tt("account_user_level_user");
      case IAccountUserLevel.admin: return tt("account_user_level_admin");
      default: return String(level);
    }
  },
};
